<template>
    <!-- 
        多點觸控優化樣式：
        - touch-action: none        → 禁用瀏覽器預設觸控手勢（縮放、滾動），確保多點觸控正常
        - user-select: none         → 防止觸控時選取文字，避免干擾遊戲
        - -webkit-touch-callout     → 禁用 iOS 長按選單（複製、貼上等）
        - -webkit-user-select       → iOS Safari 舊版兼容性，防止文字選取
    -->
    <div class="molegame-container" style="touch-action: none; user-select: none; -webkit-touch-callout: none; -webkit-user-select: none;">
        <!-- 游戏区域 -->
        <div class="game-area" ref="gameAreaRef"
            :style="{ 'background': mockImg, 'background-size': 'cover', 'background-repeat': 'no-repeat', 'background-position': 'center bottom' }">

            <div class="game-header">
                <div class="game-topic">{{ gameConfig.topic }}</div>
            </div>
            <div class="timer">{{ formatTime(timeRemaining) }}
                <button class="start-button" @click="toggleGame">
                    <svg-icon :icon-class="gameStarted ? (gamePaused ? 'play' : 'pause') : 'play'" />
                </button>
            </div>

            <!-- 遊戲開始前彈窗區 -->
            <div v-if="beforeGame" class="before-game-container">
                <div class="before-game-modal">
                    <div class="game-title">{{ gameConfig.topic }}</div>
                    <div class="game-info">
                        <div class="game-time">{{ $t('Molegame.gameDuration') }}： {{ gameConfig.gameDuration }} {{
                $t('Molegame.second') }}</div>
                        <div class="game-time">{{ $t('Molegame.moleSpeed') }}： {{ (gameConfig.moleSpeed / 1000).toFixed(1) }} {{
                $t('Molegame.second') }}</div>

                    </div>
                    <div class="game-buttons">
                        <button class="action-button play-button" @click="showReadyGoCountdown()">
                            <svg-icon icon-class="play" class="play-icon" />
                        </button>
                        <button v-if="!hasConfigUrl" class="action-button setting-button" @click="goToSettings()">
                            <svg-icon icon-class="setting-game" class="setting-icon" />
                        </button>
                        <!-- WebView 環境才顯示推送按鈕 -->
                        <button v-if="isWebView" class="action-button push-button" 
                                :class="{ 'disabled': isSendDisabled }"
                                @click="handleSendClick()">
                            <svg-icon icon-class="upload" class="sendweb-icon" />
                        </button>
                    </div>
                </div>
            </div>

            <!-- Ready, Go! 提示 -->
            <div v-if="showReadyGo" class="ready-go-container">
                <div class="ready-go-text">{{ readyGoText }}</div>
            </div>

            <!---遊戲主畫面-->
            <!-- 打地鼠遊戲區域 -->
            <div class="mole-game-container">
                <div class="score-display">
                    <div class="score-value">{{ score }}</div>
                </div>
                <!-- 半圓形草地背景 -->
                <div class="ground-background" :class="{
                    'desert-ground': gameConfig.background === 'desert',
                    'forest-ground': gameConfig.background === 'forest'
                }"></div>
                <div class="mole-holes-container">
                    <div class="mole-grid">
                        <div v-for="(hole, index) in moleHoles" :key="index" class="mole-hole">
                            <div class="mole-hole-bg"></div>
                            <div class="mole" 
                                :class="{ 'active': hole.active, 'hit': hole.hit }" 
                                :style="{ 'background-image': hole.hit ? 'url(' + require('@/assets/HTwbActAssets/MolegameObj/mole-hit.webp') + ')' : 'url(' + require('@/assets/HTwbActAssets/MolegameObj/mole.webp') + ')' }"
                                @click="hitMole(index)"
                                @touchstart="handleTouchStart($event, index)"
                                @touchend="handleTouchEnd($event)">
                                <div v-if="hole.active && !hole.hit" class="mole-text" :class="{ 'correct': hole.isCorrect, 'wrong': !hole.isCorrect }">
                                    {{ hole.text }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <!-- 飛行分數動畫 -->
                <transition-group name="flying-score" tag="div">
                    <div
                        v-for="effect in flyingScoreEffects"
                        :key="effect.id"
                        class="flying-score-effect"
                        :style="{
                            left: effect.currentX + 'px',
                            top: effect.currentY + 'px',
                            color: effect.isCorrect ? 'blue' : 'red',
                            opacity: effect.opacity
                        }"
                    >
                        {{ effect.text }}
                    </div>
                </transition-group>
            </div>

            <!-- 遊戲暫停彈窗 -->
            <div v-if="gameStarted && gamePaused && !gameOver" class="pause-game-container">
                <div class="pause-game-modal">
                    <div class="pause-title">{{ $t('Molegame.gamePaused') || '活動暫停' }}</div>
                    <div class="pause-buttons">
                        <button class="action-button continue-button" @click="resumeGame()">
                            <svg-icon icon-class="play" class="play-icon" />
                        </button>
                        <button class="action-button restart-button" @click="resetGame()">
                            <svg-icon icon-class="refresh" class="refresh-icon" />
                        </button>
                    </div>
                </div>
            </div>
            <!-- 遊戲結束彈窗 -->
            <div v-if="gameOver" class="game-over-modal">
                <div class="mole-game-result-container" v-show="!showAnswerModal">
                    <!-- 結果卡片(顯示分數,正確錯誤次數) -->
                    <div class="result-card-wrap">
                        <div class="result-card">
                            <div class="result-header">
                                <div class="mole-result-title">{{ $t('Molegame.gameOver') || '遊戲結束' }}</div>
                            </div>
                            <div class="result-score">
                                <div class="star-score">
                                    <span class="score-number">{{ score }}</span>
                                </div>
                            </div>
                            <div class="result-details">
                                <div class="detail-item">
                                    {{ $t('Molegame.correctItems') }}: {{ correctCount }} {{ $t('Molegame.items') }}
                                </div>
                                <div class="detail-item">
                                    {{ $t('Molegame.errorItems') }}: {{ wrongCount }} {{ $t('Molegame.items') }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- 答案彈窗 -->
                <div v-if="showAnswerModal" class="answer-modal-mask">
                    <div class="answer-modal-card">
                        <div class="answer-modal-header">
                            <span class="answer-modal-title">{{ $t('Molegame.referenceAnswers') }}</span>
                            <button class="answer-modal-close" @click="toggleAnswerModal">×</button>
                        </div>
                        <div class="answer-modal-section">
                            <div class="answer-label correct">✔</div>
                            <div class="answer-list">
                                <span v-for="(ans, idx) in gameConfig.correctVocabularyList" :key="'c-' + idx"
                                    class="answer-item correct">{{ ans }}</span>
                            </div>
                        </div>
                        <div class="answer-modal-section">
                            <div class="answer-label wrong">✘</div>
                            <div class="answer-list">
                                <span v-for="(ans, idx) in gameConfig.errorVocabularyList" :key="'e-' + idx"
                                    class="answer-item wrong">{{ ans }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 底部按鈕區 -->
                <div class="game-result-actions" v-show="!showAnswerModal">
                    <button class="action-button answer-button" @click="toggleAnswerModal">
                        <span class="answer-text">{{ $t('Molegame.showAnswers') }}</span>
                    </button>
                    <button class="action-button restart-button" @click="resetGame">
                        <svg-icon icon-class="refresh" class="refresh-icon" />
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted, computed, inject } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import SvgIcon from '@/components/SvgIcon.vue';

const router = useRouter();
const { t } = useI18n();
const iviewMessage = inject("$Message");

// 通信工具類
const MessageBridge = {
    // 環境類型
    ENV: {
        WEBVIEW: 'webview',
        WEB: 'web'
    },
    // 當前環境
    currentEnv: 'webview',
    // 初始化
    init() {
        this.currentEnv = window?.chrome?.webview ? this.ENV.WEBVIEW : this.ENV.WEB;
        return this;
    },
    // 發送消息
    postMessage(data, origin = '*') {
        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.postMessage(data);
        } else {
            window.postMessage(data, origin);
        }
    },
    // 添加消息監聽器
    addEventListener(callback) {
        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.addEventListener('message', event => {
                callback(event);
            });
        } else {
            window.addEventListener('message', event => {
                callback(event);
            });
        }
    }
};

// 初始化消息橋接器
const messageBridge = MessageBridge.init();

// WebView 環境檢測
const isWebView = computed(() => {
    return messageBridge.currentEnv === MessageBridge.ENV.WEBVIEW;
});

// 推送按鈕冷卻狀態
const isSendDisabled = ref(false);
const cooldownTimer = ref(null);

// 配置加載狀態
const hasConfigUrl = ref(false);
const configUrl = ref('');

// 遊戲配置
let gameConfig = reactive({
    topic: 'empty state',
    correctVocabularyList: [],
    errorVocabularyList: [],
    gameDuration: 0,
    background: '',
    moleSpeed: 2500
});

// 遊戲區域大小
const gameAreaRef = ref(null);
const gameAreaWidth = ref(0);
const gameAreaHeight = ref(0);

// 遊戲狀態
const gameStarted = ref(false);
const gamePaused = ref(false);
const gameOver = ref(false);
const beforeGame = ref(true);
const showAnswerModal = ref(false);

// 計時相關
const timeRemaining = ref(0);
const timerInterval = ref(null);

// 分數顯示相關
const score = ref(0);
const correctCount = ref(0);
const wrongCount = ref(0);
const flyingScoreEffects = ref([]);
let flyingScoreId = 0;

// Ready, Go! 倒數相關
const showReadyGo = ref(false);
const readyGoText = ref('');
const isCountingDown = ref(false);

// 地鼠洞
const moleHoles = ref([]);
const molePopInterval = ref(null);
// 地鼠相關狀態

// 音效相關
const readySound = ref(null);
const goSound = ref(null);
const backgroundMusic = ref(null);
const hitSound = ref(null);
const wrongHitSound = ref(null); // 打到錯誤答案的音效
const gameOverSound = ref(null);
const isMuted = ref(false);

// 隨機背景
const mockImg = computed(() => {
    const backgrounds = {
        forest: require('@/assets/HTwbActAssets/fantasy_forest.webp'),
        desert: require('@/assets/HTwbActAssets/desert_oasis.webp')
    };
    
    const defaultBg = require('@/assets/HTwbActAssets/fantasy_forest.webp');
    
    if (!gameConfig.background) return `url(${defaultBg})`;
    
    const bgPath = backgrounds[gameConfig.background] || defaultBg;
    return `url(${bgPath})`;
});

// 初始化地鼠洞
const initMoleHoles = () => {
    // 建立 3x3 的地鼠洞陣列（九宮格）
    moleHoles.value = Array(9).fill().map(() => ({
        active: false,
        hit: false,
        text: '',
        isCorrect: false
    }));
};

// 讓地鼠隨機彈出 - 支援多人遊戲，可同時彈出多隻地鼠
const popRandomMole = () => {
    if (!gameStarted.value || gamePaused.value || gameOver.value) return;
    
    // 取得可用的洞
    const availableHoles = moleHoles.value
        .map((hole, index) => ({ hole, index }))
        .filter(item => !item.hole.active && !item.hole.hit);
    
    if (availableHoles.length === 0) return;
    
    // 決定這次要彈出幾隻地鼠 (1-3隻，根據可用洞數調整)
    const maxMoles = Math.min(3, availableHoles.length);
    const moleCount = Math.floor(Math.random() * maxMoles) + 1;
    
    // 隨機選擇要彈出地鼠的洞
    const selectedHoles = [];
    const availableIndices = [...availableHoles];
    
    for (let i = 0; i < moleCount; i++) {
        if (availableIndices.length === 0) break;
        
        const randomIndex = Math.floor(Math.random() * availableIndices.length);
        const selectedHole = availableIndices.splice(randomIndex, 1)[0];
        selectedHoles.push(selectedHole);
    }
    
    // 為每個選中的洞設定地鼠
    selectedHoles.forEach(({ index: holeIndex }) => {
        // 決定是正確還是錯誤詞彙
        const isCorrect = Math.random() < 0.6; // 60% 機率是正確詞彙
        
        const wordPool = isCorrect ? gameConfig.correctVocabularyList : gameConfig.errorVocabularyList;
        
        if (!wordPool || wordPool.length === 0) return;
        
        const randomWord = wordPool[Math.floor(Math.random() * wordPool.length)];
        
        // 設定地鼠狀態
        moleHoles.value[holeIndex] = {
            active: true,
            hit: false,
            text: randomWord,
            isCorrect: isCorrect
        };
        
        // 設定地鼠自動縮回（根據配置的速度調整停留時間）
        const baseHideTime = gameConfig.moleSpeed || 2500;
        const hideTime = baseHideTime + Math.random() * 1000; // 基礎時間 + 隨機0-1秒
        setTimeout(() => {
            if (moleHoles.value[holeIndex] && moleHoles.value[holeIndex].active && !moleHoles.value[holeIndex].hit) {
                moleHoles.value[holeIndex].active = false;
            }
        }, hideTime);
    });
};

// 觸控處理標記，避免重複觸發
const processedTouches = ref(new Set());

// 簡化的觸控開始處理 - 立即觸發，支援多點觸控
const handleTouchStart = (event, index) => {
    if (!gameStarted.value || gamePaused.value || gameOver.value) return;
    
    // 遍歷所有新的觸控點
    for (let i = 0; i < event.changedTouches.length; i++) {
        const touch = event.changedTouches[i];
        const touchId = touch.identifier;
        
        // 防止重複處理同一個觸控點
        if (!processedTouches.value.has(touchId)) {
            processedTouches.value.add(touchId);
            
            // 立即觸發打擊，不等 touchend
            hitMole(index);
            
            // 延遲清除觸控標記
            setTimeout(() => {
                processedTouches.value.delete(touchId);
            }, 100);
        }
    }
    
    // 阻止預設行為，避免觸發 click 事件
    event.preventDefault();
    event.stopPropagation();
};

// 觸控結束處理 - 清理狀態
const handleTouchEnd = (event) => {
    // 清理結束的觸控點標記
    for (let i = 0; i < event.changedTouches.length; i++) {
        const touch = event.changedTouches[i];
        processedTouches.value.delete(touch.identifier);
    }
};

// 點擊地鼠 - 支援多人同時點擊不同地鼠
const hitMole = (index) => {
    if (!gameStarted.value || gamePaused.value || gameOver.value) return;
    
    const hole = moleHoles.value[index];
    
    // 原子性檢查並立即標記，防止多點觸控重複處理
    if (!hole.active || hole.hit) return;
    hole.hit = true; // 立即標記為已被打中，防止重複點擊
    
    // 取得遊戲區域
    const gameArea = gameAreaRef.value;
    const gameRect = gameArea.getBoundingClientRect();
    
    // 取得地鼠位置 (大約估算)
    const holeWidth = gameRect.width / 3;
    const holeHeight = gameRect.height / 3;
    const row = Math.floor(index / 3);
    const col = index % 3;
    
    const itemX = col * holeWidth + holeWidth / 2 + gameRect.left;
    const itemY = row * holeHeight + holeHeight / 2 + gameRect.top;
    
    // 決定分數
    let effectText = '';
    let isCorrect = false;
    
    if (hole.isCorrect) {
        effectText = '+10';
        isCorrect = true;
        score.value += 10;
        correctCount.value++;
    } else {
        effectText = '-5';
        isCorrect = false;
        score.value = Math.max(0, score.value - 5); // 防止分數為負
        wrongCount.value++;
    }
    
    // 正確答案播放打擊音效
    if (hole.isCorrect) {
        playSound(hitSound.value);
    } else {
        // 錯誤答案播放尖叫音效
        playSound(wrongHitSound.value);
    }
    
    // 新增飛行分數效果 - 固定在點擊位置附近
    const id = flyingScoreId++;
    const effect = {
        id,
        text: effectText,
        isCorrect,
        currentX: itemX,
        currentY: itemY - 30, // 顯示在點擊位置上方一點
        opacity: 1
    };
    flyingScoreEffects.value.push(effect);
    
    // 簡化動畫，只做淡出效果，不移動位置
    const duration = 1000;
    setTimeout(() => {
        // 淡出效果
        const fadeOutDuration = 300;
        let opacity = 1;
        const fadeInterval = setInterval(() => {
            opacity -= 0.1;
            if (opacity <= 0) {
                clearInterval(fadeInterval);
                // 移除效果
                flyingScoreEffects.value = flyingScoreEffects.value.filter(e => e.id !== id);
            } else {
                // 更新透明度
                const effectIndex = flyingScoreEffects.value.findIndex(e => e.id === id);
                if (effectIndex !== -1) {
                    flyingScoreEffects.value[effectIndex].opacity = opacity;
                }
            }
        }, fadeOutDuration / 10);
    }, duration - 300); // 顯示一段時間後開始淡出
    
    // 一段時間後讓地鼠縮回
    setTimeout(() => {
        hole.active = false;
        hole.hit = false;
    }, 300); // 縮短時間讓地鼠更快消失，提升遊戲節奏
    
    // 播放打擊音效
    playSound(hitSound.value);
};

// 格式化时间显示
const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// 切換遊戲狀態（開始/暫停/繼續）
const toggleGame = () => {
    if (!gameStarted.value) {
        // 如果遊戲還沒開始，則顯示Ready, Go!倒數
        showReadyGoCountdown();
    } else if (!gamePaused.value) {
        // 如果遊戲正在進行，則暫停
        pauseGame();
    } else {
        // 如果遊戲已暫停，則繼續
        resumeGame();
    }
};

// 顯示Ready, Go!倒數效果
const showReadyGoCountdown = () => {
    // 停止並重置所有音效
    if (readySound.value) {
      readySound.value.pause();
      readySound.value.currentTime = 0;
    }
    if (goSound.value) {
      goSound.value.pause();
      goSound.value.currentTime = 0;
    }
    if (backgroundMusic.value) {
      backgroundMusic.value.pause();
      backgroundMusic.value.currentTime = 0;
    }
    if (hitSound.value) {
      hitSound.value.pause();
      hitSound.value.currentTime = 0;
    }
    if (wrongHitSound.value) {
      wrongHitSound.value.pause();
      wrongHitSound.value.currentTime = 0;
    }
    if (gameOverSound.value) {
      gameOverSound.value.pause();
      gameOverSound.value.currentTime = 0;
    }
    
    if (isCountingDown.value) return;
    
    isCountingDown.value = true;
    showReadyGo.value = true;
    beforeGame.value = false;
    
    // 顯示Ready...
    readyGoText.value = 'Ready...';
    // 播放Ready音效
    playSound(readySound.value);
    
    // 2秒後顯示Go!
    setTimeout(() => {
        readyGoText.value = 'Go!';
        // 播放Go音效
        playSound(goSound.value);
        
        // 1秒後開始遊戲
        setTimeout(() => {
            showReadyGo.value = false;
            isCountingDown.value = false;
            startGame();
        }, 1000);
    }, 2000);
};

// 開始遊戲
const startGame = () => {
    // 初始化遊戲
    gameStarted.value = true;
    gamePaused.value = false;
    gameOver.value = false;
    beforeGame.value = false;
    
    // 重設分數
    score.value = 0;
    correctCount.value = 0;
    wrongCount.value = 0;
    
    // 初始化地鼠洞
    initMoleHoles();
    
    // 播放背景音樂
    playSound(backgroundMusic.value);
    
    // 設定計時器
    timeRemaining.value = gameConfig.gameDuration;
    clearInterval(timerInterval.value);
    timerInterval.value = setInterval(() => {
        if (timeRemaining.value > 0) {
            timeRemaining.value--;
        } else {
            endGame();
        }
    }, 1000);
    
    // 設定地鼠彈出定時器
    clearInterval(molePopInterval.value);
    molePopInterval.value = setInterval(() => {
        popRandomMole();
    }, gameConfig.moleSpeed || 1000); // 使用配置的地鼠速度
};

// 暫停遊戲
const pauseGame = () => {
    if (!gameStarted.value || gameOver.value) return;
    
    gamePaused.value = true;
    
    // 暫停計時器
    clearInterval(timerInterval.value);
    
    // 暫停地鼠彈出
    clearInterval(molePopInterval.value);
    
    // 暫停背景音樂
    if (backgroundMusic.value) {
        backgroundMusic.value.pause();
    }
};

// 繼續遊戲
const resumeGame = () => {
    if (!gameStarted.value || gameOver.value || !gamePaused.value) return;
    
    gamePaused.value = false;
    
    // 繼續背景音樂
    if (backgroundMusic.value && !isMuted.value) {
        backgroundMusic.value.play().catch(error => console.error('繼續播放背景音樂失敗:', error));
    }
    
    // 繼續計時器
    timerInterval.value = setInterval(() => {
        if (timeRemaining.value > 0) {
            timeRemaining.value--;
        } else {
            endGame();
        }
    }, 1000);
    
    // 繼續地鼠彈出 - 使用配置的地鼠速度
    molePopInterval.value = setInterval(() => {
        popRandomMole();
    }, gameConfig.moleSpeed || 1000);
};

// 結束遊戲
const endGame = () => {
    gameStarted.value = false;
    gameOver.value = true;
    
    // 停止計時器
    clearInterval(timerInterval.value);
    
    // 停止地鼠彈出
    clearInterval(molePopInterval.value);
    
    // 停止背景音樂
    stopSound(backgroundMusic.value);
    
    // 播放遊戲結束音效
    playSound(gameOverSound.value);
    
    // 隱藏所有地鼠
    moleHoles.value.forEach(hole => {
        hole.active = false;
        hole.hit = false;
    });
};

// 重置遊戲
const resetGame = () => {
    gameStarted.value = false;
    gamePaused.value = false;
    gameOver.value = false;
    beforeGame.value = true;
    showAnswerModal.value = false;
    
    // 清除計時器
    clearInterval(timerInterval.value);
    clearInterval(molePopInterval.value);
    
    // 清理觸控狀態
    processedTouches.value.clear();
    
    // 停止所有音效
    stopSound(backgroundMusic.value);
    stopSound(wrongHitSound.value);
    stopSound(gameOverSound.value);
    
    // 重設分數
    score.value = 0;
    correctCount.value = 0;
    wrongCount.value = 0;
    
    // 重設時間
    timeRemaining.value = gameConfig.gameDuration;
    
    // 重設地鼠洞
    initMoleHoles();
};

// 切換答案彈窗顯示
const toggleAnswerModal = () => {
    showAnswerModal.value = !showAnswerModal.value;
};

// 前往設置頁面
const goToSettings = () => {
    router.push('/ht-molegame-setting');
};

// 推送到終端
const sendToWebIRS = () => {
    // console.log({action: 'Send.WebIRS', data: {game: 'moleGame', gameConfig: JSON.stringify(gameConfig)}})
    messageBridge.postMessage({action: 'SendGame.WebIRS', data: {game: 'moleGame', gameConfig: JSON.stringify(gameConfig)}});
};

// 處理推送按鈕點擊
const handleSendClick = () => {
    console.log('按鈕點擊，當前禁用狀態：', isSendDisabled.value);
    
    if (isSendDisabled.value) {
        iviewMessage.warning({
            content: t('Molegame.sendCooldown'),
            duration: 3,
            closable: true
        });
        return;
    }
    
    // 執行推送
    sendToWebIRS();
    iviewMessage.success({
        content: t('Molegame.sendSuccess'),
        duration: 3,
        closable: true
    });
    
    // 設置冷卻狀態
    isSendDisabled.value = true;
    console.log('設置為禁用狀態：', isSendDisabled.value);
    
    // 1分鐘後恢復
    cooldownTimer.value = setTimeout(() => {
        isSendDisabled.value = false;
        console.log('恢復為啟用狀態：', isSendDisabled.value);
    }, 60000); // 60秒
};

// 初始化音效
const initSounds = () => {
    // 創建音效對象
    readySound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/freeTTS-ready.mp3'));
    goSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/freeTTS-go.mp3'));
    backgroundMusic.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/game-176807.mp3'));
    hitSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/punch-41105.mp3'));
    wrongHitSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/cartoon-scream-1-6835.mp3'));
    gameOverSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/orchestral-win-331233.mp3'));
    
    // 設置背景音樂循環播放
    backgroundMusic.value.loop = true;
    
    // 預加載音效
    readySound.value.load();
    goSound.value.load();
    backgroundMusic.value.load();
    hitSound.value.load();
    wrongHitSound.value.load();
    gameOverSound.value.load();
};

// 播放音效的方法
const playSound = (sound) => {
    if (isMuted.value || !sound) return;
    
    // 先暫停音效
    sound.pause();
    sound.currentTime = 0;
    
    // 使用 setTimeout 延遲播放，避免播放和暫停操作衝突
    setTimeout(() => {
        sound.play().catch(() => {});
    }, 10);
};

// 停止音效的方法
const stopSound = (sound) => {
    if (!sound) return;
    
    sound.pause();
    sound.currentTime = 0;
};

// 從 blob URL 載入配置
const loadConfigFromUrl = async (url) => {
    try {
        const response = await fetch(url);
        
        if (!response.ok) {
            throw new Error('載入失敗');
        }
        
        const config = await response.json();
        Object.assign(gameConfig, JSON.parse(config.data.gameConfig));
        timeRemaining.value = gameConfig.gameDuration;
        
        iviewMessage.success({
            content: t('Molegame.configLoadSuccess'),
            duration: 2,
            closable: true
        });
    } catch (error) {
        iviewMessage.error({
            content: t('Molegame.configLoadFailed'),
            duration: 3,
            closable: true
        });
        
        // 使用預設配置
        Object.assign(gameConfig, {
            topic: "Choose Fruits (Demo)",
            correctVocabularyList: ["apple", "banana", "orange", "grape", "watermelon", "strawberry", "blueberry", "pineapple", "kiwi", "mango"],
            errorVocabularyList: ["pen", "pencil", "notebook", "eraser", "ruler", "scissors", "calculator", "backpack", "paper", "stapler"],
            gameDuration: 30,
            background: "forest",
            moleSpeed: 2500
        });
        timeRemaining.value = gameConfig.gameDuration;
    }
};

// 組件掛載時
onMounted(async () => {
    // 初始化音效
    initSounds();
    
    // 初始化遊戲區域尺寸
    if (gameAreaRef.value) {
        const rect = gameAreaRef.value.getBoundingClientRect();
        gameAreaWidth.value = rect.width;
        gameAreaHeight.value = rect.height;
    }
    
    // 初始化地鼠洞
    initMoleHoles();
    
    // 檢查是否有 config_url 參數 - 使用原本的方式
    const fullUrl = window.location.href;
    const configUrlIndex = fullUrl.indexOf('config_url=');
    
    if (configUrlIndex !== -1) {
        // 取得 config_url= 之後的所有內容（直到字串結束）
        configUrl.value = fullUrl.substring(configUrlIndex + 'config_url='.length);
        hasConfigUrl.value = true;
        await loadConfigFromUrl(configUrl.value);
    } else {
        hasConfigUrl.value = false;
        
        // 發送初始化訊息，並接收遊戲配置
        messageBridge.postMessage({ action: 'Init.MoleGame', data: { 
            topic: "Choose Fruits (Demo)",
            correctVocabularyList: ["apple", "banana", "orange", "grape", "watermelon", "strawberry", "blueberry", "pineapple", "kiwi", "mango"],
            errorVocabularyList: ["pen", "pencil", "notebook", "eraser", "ruler", "scissors", "calculator", "backpack", "paper", "stapler"],
            gameDuration: 30,
            background: "forest",
            moleSpeed: 2500
        } });
        
        messageBridge.addEventListener(event => {
            const msg = event.data;
            if (msg.action === 'Init.MoleGame') {
                Object.assign(gameConfig, msg.data);
                timeRemaining.value = gameConfig.gameDuration;
                // 將設定存入 sessionStorage
                sessionStorage.setItem('MoleGameConfig', JSON.stringify(msg.data));
            } 
        });
    }
    
    // 監聽視窗大小變化
    window.addEventListener('resize', () => {
        if (gameAreaRef.value) {
            const rect = gameAreaRef.value.getBoundingClientRect();
            gameAreaWidth.value = rect.width;
            gameAreaHeight.value = rect.height;
        }
    });
});

// 組件卸載時
onUnmounted(() => {
    // 清理計時器
    clearInterval(timerInterval.value);
    clearInterval(molePopInterval.value);
    clearTimeout(cooldownTimer.value);
    
    // 清理觸控狀態
    processedTouches.value.clear();
    
    // 移除視窗大小變化監聽
    window.removeEventListener('resize', () => {});
    
    // 停止所有音效
    stopSound(readySound.value);
    stopSound(goSound.value);
    stopSound(backgroundMusic.value);
    stopSound(hitSound.value);
    stopSound(wrongHitSound.value);
    stopSound(gameOverSound.value);
});
</script>

<style lang="less" scoped>
@import '../assets/HTwbActKitCSS/HTMoleGame.less';
</style>